import { apiPost } from './client';
import type { JoinStartFields } from './sync';

export interface StartTaskResult {
  id: string;
  state: number;
  tour_operator_id: number | null;
}

export async function startOTAImport(body: {
  tour_operator_id?: number | null;
  format?: string;
  mapping?: Record<string, unknown>;
  rows?: Record<string, unknown>[];
  redis_key?: string;
}): Promise<StartTaskResult> {
  return apiPost('/ops/ota_import/start/', body);
}

export interface SyncBookingsStartInput {
  tour_operator_id: number;
  integration_name: string;
  account?: string | null;
  /** Booking-start range — ISO date (YYYY-MM-DD) or datetime (naive → UTC). */
  from_date?: string;
  to_date?: string;
  /** Updated/created range (ISO datetime) — only for integrations whose
   *  booking_query declares updated_range; combining with a start range
   *  requires booking_query.combined. */
  updated_from?: string;
  updated_to?: string;
  product_ids?: string[];
  booking_ids?: string[];
  /** true → FetchBookingsTask: fetch + XML artifacts, no import. */
  dry_run?: boolean;
  approval_required?: boolean;
  /** Import_Source code override (defaults to the integration config). */
  source_id?: string | null;
  send_invitations?: boolean;
  send_surveys?: boolean;
  statuses?: string[];
  reconcile_existing?: boolean;
  batch_multiplier?: number;
  extra_args?: Record<string, unknown>;
}

export async function startSyncBookings(body: SyncBookingsStartInput): Promise<StartTaskResult> {
  return apiPost('/ops/sync_bookings/start/', body);
}

export interface SyncOrdersStartInput {
  tour_operator_id: number;
  integration_name: string;
  account?: string | null;
  /** Order updated-at window (ISO datetime) — open end defaults to now. */
  updated_from?: string;
  updated_to?: string;
  /** Order created-at window (ISO datetime) — open end defaults to now. */
  created_from?: string;
  created_to?: string;
  /** Explicit remote order IDs. */
  order_ids?: string[];
  /** External product IDs — order IDs are derived from the bookings listing;
   *  requires both from_date and to_date (travel-date range). */
  product_ids?: string[];
  from_date?: string;
  to_date?: string;
  /** Refetch orders already present in the local DB. */
  reconcile_existing?: boolean;
  /** With reconcile_existing — only refetch orders in these travel-time
   *  groups (orders with no dated bookings count as past). Absent = all. */
  reconcile_groups?: ('past' | 'active' | 'future')[];
  /** true → FetchOrdersTask: fetch + artifacts, no processing. */
  dry_run?: boolean;
  approval_required?: boolean;
  /** queue (default) = replay via the per-order webhook queue, serialized with
   *  live webhooks; inline = process in the task pod with per-order results. */
  processing_mode?: 'queue' | 'inline';
  send_invitations?: boolean;
  send_surveys?: boolean;
}

export async function startSyncOrders(body: SyncOrdersStartInput): Promise<StartTaskResult> {
  return apiPost('/ops/sync_orders/start/', body);
}

/** `joins` / `split_off` / `booking_move` (`JoinStartFields`, plan 2026-10-11
 *  product-sync joins §1) are required: every run sends them, `[]` and null
 *  included, whatever the integration supports, so a staged wizard payload
 *  never keeps an earlier run's joins. */
export interface SyncProductsStartInput extends JoinStartFields {
  tour_operator_id: number;
  integration_name: string;
  account?: string | null;
  product_ids: string[];
  /** Build + upload per-product XML artifacts and stop. Wins over approval. */
  dry_run?: boolean;
  /** Build, pause in AWAITING_APPROVAL for artifact review, import on approval. */
  approval_required?: boolean;
  /** Catalogue entity kinds to build; omit for all. Needs the
   *  `product_entity_selection` feature — the backend 400s otherwise. */
  entities?: string[];
  /** Default true: merge the built XML into one Import_Run fanned out via
   *  plimport. False = sequential fallback, one importer run per product. */
  use_plimport?: boolean;
  /** Advanced, parallel mode only: plimport batches run concurrently (1–10). */
  batch_multiplier?: number;
  /** Advanced: concurrent vendor fetches in the build step (1–8). */
  fetch_pool_size?: number;
  /** Seconds between individual product imports (0–300); sequential mode only. */
  import_delay?: number;
  source_id?: string | null;
  /** Run the integration's AI enrichment pass. METERED — one model call
   *  per product; the running cost lands in metrics.ai_enrichment.cost_usd.
   *  Needs the `product_ai_enrichment` feature. */
  enrich_with_ai?: boolean;
  /** Which external-ID form the created tours carry. Validated server-side
   *  against what this integration's product builder can actually mint — a
   *  qualified form means one tour per departure time, which no builder emits
   *  yet, so those are refused with a 400 rather than silently downgraded. */
  mapping_policy?: string;
  extra_args?: Record<string, unknown>;
}

export async function startSyncProducts(body: SyncProductsStartInput): Promise<StartTaskResult> {
  return apiPost('/ops/sync_products/start/', body);
}
